import React, { useCallback, useEffect, useState } from 'react'
import { ActivityIndicator, ScrollView } from 'react-native'

import { FlashList } from '@shopify/flash-list'
import { useQueryClient } from '@tanstack/react-query'
import TransparentHeader from '@zet/common/components/header/transparentHeader'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { colors as zetColors } from '@zet/design-system'
import { Icon } from '@zet/design-system/icons'
import { Input } from '@zet/design-system/input'
import { Stack } from '@zet/design-system/layout'
import {
  useFetchFilterFields,
  useLeads,
} from '@zet/feature-leads/api/leads/hooks'
import EmptyLeads from '@zet/feature-leads/components/emptyLeads'
import { LeadCard } from '@zet/feature-leads/components/leadCard'
import LeadsFilterTab from '@zet/feature-leads/components/leadsFilterTab'
import useLeadFilterStore from '@zet/feature-leads/store/leadFilter'
import { resetQuickFilters } from '@zet/feature-leads/utils/resetQuickFilters'
import { i18n } from '@zet/localization'
import { LinearGradient } from 'expo-linear-gradient'
import _ from 'lodash'
import moment from 'moment'

const renderItem = ({ item }: any) => {
  return (
    <LeadCard
      key={item?.id}
      lastUpdateDate={moment.utc(item?.lastUpdatedDate).format('DD-MMM-YYYY')}
      nextUpdateDate={moment.utc(item?.nextUpdateDate).format('DD-MMM-YYYY')}
      name={item?.name}
      phoneNumber={item?.phoneNumber}
      status={item?.status}
      isBonusLead={item?.isBonusLead}
      subAgentName={item?.subAgentName}
      hasNotMoved={item?.hasNoUpdate}
      paymentStatus={item?.partnerTransaction?.[0].status ?? ''}
      rejectionReason={item?.rejectionReason}
      pendingDays={item?.expiryDays}
      isDelayedByBrand={item?.isReportDelayed}
      brandCommission={item?.brandCommission}
      commission={item?.partnerTransaction?.[0]?.commission ?? ''}
      title={item?.applicationAsks?.[0]?.title ?? ''}
      description={item?.applicationAsks?.[0]?.description ?? ''}
      isReopenAllowed={item?.isReopenAllowed}
      isShareNewLink={item?.isShareNewLink}
      isReopened={item?.isReopened}
      id={item?.id}
      partnerId={item?.partnerId}
      commissionType={item?.commissionType}
      eventName={'click_action'}
      eventPayload={{
        item_name: 'my_customers_brand_my_leads_lead_card',
        lead_id: item?.id,
        lead_status: item?.status,
        partner_id: item?.partnerId,
      }}
      askCode={item?.applicationAsks?.[0]?.askCode}
      extraCommission={item?.extraCommission}
    />
  )
}

type BrandLeadParams = {
  route: {
    params: {
      brandName: string
    }
  }
}

const BrandLeads = ({ route }: BrandLeadParams) => {
  const [searchedText, setSearchedText] = useState<string>('')
  const leadFilterStore = useLeadFilterStore()
  const navigation = useNavigation()
  const queryClient = useQueryClient()

  const {
    data: leadsData,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useLeads({ searchText: searchedText?.trim() })

  const leadCardList = leadsData?.pages?.map((page) => page?.leads).flat() || []

  const { data: filters = [] } = useFetchFilterFields()

  const renderQuickFilters = () => {
    return Object.keys(filters)
      .filter((key) => key !== 'brands' && key !== 'category')
      .map((item) => (
        <LeadsFilterTab
          key={item}
          filterName={item}
          filterOptions={filters[item]}
        />
      ))
  }

  const debouncedFetch = useCallback(
    _.debounce((query) => {
      setSearchedText(query)
    }, 600),
    [refetch]
  )

  const handleInputChange = (query: string) => {
    debouncedFetch(query)
  }

  useEffect(() => {
    refetch()
  }, [leadsData])

  const handleBack = () => {
    leadFilterStore.resetLeadFilters()
    resetQuickFilters()
    navigation.goBack()
    queryClient.invalidateQueries({ queryKey: ['home-widgets'] })
    queryClient.invalidateQueries({ queryKey: ['leads'] })
    return
  }

  return (
    <Stack className=" bg-grays-20 flex-1 ">
      <LinearGradient
        colors={[zetColors.tertiary[100], zetColors.tertiary[100]]}
        start={[0, 0]}
        end={[1, 0.0]}
        locations={[0.0, 1.0]}
      >
        <TransparentHeader
          handleBack={handleBack}
          showCloseIcon
          title={`${route.params?.brandName} Leads`}
        />
        <Stack className="-mt-3  px-3">
          <Input
            iconLeft={<Icon type="SearchOutlined" color="white" />}
            color="#FFFFFF"
            selectionColor={'grey'}
            className=" mb-3 border-[#f5f7fb7c] bg-[#1A304D]"
            placeholder={i18n.t('myCustomers.placeholder')}
            frameStyle={{
              backgroundColor: '#1A304D',
              borderColor: '#f5f7fb7c',
              marginBottom: 16,
            }}
            onChangeText={handleInputChange}
          />
        </Stack>
      </LinearGradient>

      {filters && (
        <ScrollView
          showsHorizontalScrollIndicator={false}
          horizontal={true}
          className="bg-neutral-white100 flex-grow-0 pl-3 "
          keyboardDismissMode="on-drag"
          contentContainerStyle={{
            paddingRight: 8,
            right: 10,
          }}
        >
          {renderQuickFilters()}
        </ScrollView>
      )}

      <FlashList
        estimatedItemSize={100}
        renderItem={renderItem}
        data={leadCardList}
        ListEmptyComponent={!isFetching ? <EmptyLeads isFromBrand /> : <></>}
        ListFooterComponent={
          isFetchingNextPage ? (
            <ActivityIndicator style={{ marginTop: 18 }} size={'large'} />
          ) : (
            <></>
          )
        }
        onEndReached={() => {
          if (!isFetching && hasNextPage) {
            fetchNextPage()
          }
        }}
        onRefresh={() => refetch()}
        refreshing={isFetching && !isFetchingNextPage}
        onEndReachedThreshold={0.6}
        showsHorizontalScrollIndicator={false}
      />
    </Stack>
  )
}

export { BrandLeads }
